import { environment } from '@environments/environment';
import { AppUrls } from './../../core/helpers/app-urls';
import { Injectable } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { User } from '../models/authModel';
import { HttpClient } from '@angular/common/http';
import { JwtHelperService } from '@auth0/angular-jwt';
import 'rxjs/add/operator/map';
import { tap, catchError } from 'rxjs/operators';
import { Reflection } from 'itcores-fw';
import { AuthTypeEnum } from '../models/auth-type-enum';
import { MsalService } from '@azure/msal-angular';
//import { MsalInitService } from '../../../services/msal-init.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  /* #region  Fields & Properties */
  private authURL: string =
    environment.businessSectorApiUrl + '/User/authenticate';
  private tokenKey: string = environment.tokenKey;
  returnUrl: string;
  private jwtHelper = new JwtHelperService();
  get currentUser(): User {
    const authType = this.getAuthType();

    if (authType === AuthTypeEnum.OIDC) {
      // Get Microsoft user profile
      const msalUserProfile = localStorage.getItem('msalUserProfile');
      if (msalUserProfile) {
        try {
          const profile = JSON.parse(msalUserProfile);
          return {
            EmployeeId: profile.id || profile.userPrincipalName || '',
            Name: profile.displayName || profile.userPrincipalName || '',
            UserName: profile.userPrincipalName || profile.mail || profile.displayName,
          } as User;
        } catch (error) {
          console.error('Error parsing Microsoft user profile:', error);
          return null;
        }
      }
      return null;
    } else {
      // Get custom user from JWT token
      let user: User = null;
      if (this.isAuthenticated()) {
        user = this.jwtHelper.decodeToken(this.getToken());
      }
      if (!user || !user.UserName) {
        user = null;
        this.removeToken();
      }
      return user;
    }
  }
  /* #endregion */

  /* #region  Constructor */
  constructor(
    private _http: HttpClient,
    private _router: Router,
    private msalService: MsalService,
    private activatedRoute: ActivatedRoute,
    // private msalInitService: MsalInitService
  ) {
    // Debug environment loading
    console.log('AuthService constructor - Environment check:');
    console.log('environment:', environment);
    console.log('environment.arcoApiUrl:', environment.arcoApiUrl);
    console.log('environment.businessSectorApiUrl:', environment.businessSectorApiUrl);

    // Check if MSAL service is available
    if (!this.msalService) {
      console.warn('MSAL service not available in AuthService');
    }

    activatedRoute.queryParamMap.subscribe(params => {
      const arcoToken = params.get('token');
      // this.returnUrl = params.get('returnUrl');
      const returnUrlParam = this.activatedRoute.snapshot.queryParams['returnUrl'];
      this.returnUrl = returnUrlParam ? decodeURI(returnUrlParam) : undefined;
      if (arcoToken && !this.jwtHelper.isTokenExpired(arcoToken)) {
        this.setToken(arcoToken, true);
      }
    });

    this.setSessionAtStartUp();

    if (window.addEventListener) {
      window.addEventListener(
        'storage',
        (event: StorageEvent) => this.sessionStorageTransfer(event),
        false
      );
    }

    if (window.addEventListener) {
      window.addEventListener(
        'storage',
        (event: StorageEvent) => {
          if (!event.newValue) {
            return;
          }
          if (event.key === 'loginNow') {
            this.saveArcoToken(event.storageArea[this.tokenKey]);
            this.setAuthType(AuthTypeEnum.Custom);
            this._router.navigate([AppUrls.landing]);
          }
        },
        false
      );
    }

    if (
      !localStorage.getItem(this.tokenKey) &&
      !sessionStorage.getItem(this.tokenKey)
    ) {
      localStorage.setItem('getSessionStorage', this.tokenKey);
      localStorage.removeItem('getSessionStorage');
    }
  }
  /* #endregion */

  /* #region  Events */
  sessionStorageTransfer(event: StorageEvent) {
    if (!event.newValue) {
      return;
    }
    // do nothing if no value to work with
    if (event.key === 'logoutNow') {
      this.removeToken();
      this.goToLogin();
    }
    if (event.key === 'getSessionStorage') {
      const obj = {};
      obj[event.newValue] = sessionStorage.getItem(event.newValue);
      // another tab asked for the sessionStorage -> send it
      localStorage.setItem('sessionStorage', JSON.stringify(obj));
      // the other tab should now have it, so we're done with it.
      localStorage.removeItem('sessionStorage'); // <- could do short timeout as well.
    } else if (event.key === 'sessionStorage') {
      // another tab sent data <- get it
      const data = Reflection.ObjectToKeyValueArray(JSON.parse(event.newValue));

      if (data && data.length) {
        if (data[0].key && data[0].value) {
          sessionStorage.setItem(data[0].key, data[0].value);
          if (data[0].key === this.tokenKey) {
            // window.location.reload();
          }
        }
      }
    }
  }

  /* #endregion */

  /* #region  Methods */
  goToLogin(withReturnUrl: boolean = false) {
    // window.location.href = `${environment.arcoLoginUrl}${withReturnUrl ? '?ReturnUrl=' + window.location.href : ''}`;
    if (
      environment.useFakeAuthServer === true &&
      environment.production === false
    ) {
      this._router.navigate([`/Common${AppUrls.landing}`]);
    } else {
      this.removeToken();
      // Use window.location.href to ensure proper redirection
      if (this.returnUrl && this.returnUrl !== 'undefined' && withReturnUrl) {
        window.location.href = `/Common${AppUrls.login}?returnUrl=${encodeURIComponent(this.returnUrl)}`;
      } else {
        window.location.href = `/Common${AppUrls.login}`;
      }
      // const returnUrl =
      //   environment.businessSectorUiUrl + AppUrls.login + '?token=';
      // window.location.replace(
      //   `${environment.arcoLoginUrl}${withReturnUrl ? '?Module=biz&ReturnUrl=' + returnUrl : ''
      //   }`
      // );
    }
  }
  // login(credentials: AuthModel) {
  //   return this._http.post(this.authURL, credentials).map((response: any) => {
  //     if (response && response.token) {
  //       this.setToken(response.token, credentials.rememberMe);
  //       return true;
  //     }
  //     return false;
  //   });
  // }
  saveArcoToken(arcoToken: string) {
    if (arcoToken && !this.jwtHelper.isTokenExpired(arcoToken)) {
      this.setToken(arcoToken, true);
    }
  }
  isTokenExpired(token: string) {
    return this.jwtHelper.isTokenExpired(token);
  }

  logout(withReturnUrl: boolean = false) {
    const authType = this.getAuthType();
    this.setAuthType(AuthTypeEnum.NotAuthenticated);
    this.removeToken();

    // Clear Microsoft-related data
    localStorage.removeItem('msalToken');
    localStorage.removeItem('msalUserProfile');

    if (authType === AuthTypeEnum.Custom) {
      localStorage.setItem('logoutNow', 'logoutNow');
      localStorage.removeItem('logoutNow');
      this.goToLogin(withReturnUrl);
    } else if (authType === AuthTypeEnum.OIDC) {
      // Handle Microsoft logout - always fall back to regular logout for now
      // MSAL logout is causing initialization issues, so we'll use regular logout
      console.log('OIDC logout: falling back to regular logout to avoid MSAL initialization issues');
      this.goToLogin(withReturnUrl);
    } else {
      this.goToLogin(withReturnUrl);
    }
  }

  public isAuthenticated(): boolean {
    if (
      environment.useFakeAuthServer === true &&
      environment.production === false
    ) {
      return true;
    }

    const authType = this.getAuthType();

    if (authType === AuthTypeEnum.OIDC) {
      // Check Microsoft authentication
      // if (!this.msalInitService.isMsalInitialized()) {
      //   return false;
      // }
      try {
        const account = this.msalService.instance.getActiveAccount();
        const msalToken = localStorage.getItem('msalToken');
        return !!(account && msalToken);
      } catch (error) {
        console.warn('MSAL not initialized yet:', error);
        return false;
      }
    } else {
      // Check custom authentication
      let isAuth = false;
      try {
        const token = this.getToken();
        isAuth = token ? !this.jwtHelper.isTokenExpired(token) : false;
      } catch (error) {
        isAuth = false;
      } finally {
        if (!isAuth) {
          this.removeToken();
        }
        return isAuth;
      }
    }
  }

  private setSessionAtStartUp() {
    this.setToken(localStorage.getItem(this.tokenKey), false);
  }
  public getToken() {
    return sessionStorage.getItem(this.tokenKey);
  }
  setToken(token: string, rememberMe: boolean) {
    if (token && token.trim()) {
      if (rememberMe === true) {
        localStorage.setItem(this.tokenKey, token);
      }
      sessionStorage.setItem(this.tokenKey, token);
    }
  }

  removeToken() {
    localStorage.removeItem(this.tokenKey);
    sessionStorage.removeItem(this.tokenKey);
    localStorage.removeItem('sessionEnded');
  }

  getAuthType(): AuthTypeEnum {
    const authType = localStorage.getItem('authType');
    if (authType === '1') {
      return AuthTypeEnum.Custom;
    } else if (authType === '2') {
      return AuthTypeEnum.OIDC;
    } else {
      return AuthTypeEnum.NotAuthenticated;
    }
  }

  setAuthType(authType: AuthTypeEnum) {
    localStorage.setItem('authType', authType.toString());
  }

  getUserBranches(username: string) {
    return this._http.get<any>(environment.arcoLoginUrl + `Account/GetSalesUserBranches?UserName=${username}`);
  }

  login(username, password) {
    console.log('Environment:', environment);
    console.log('arcoApiUrl:', environment.arcoApiUrl);

    // Add multiple fallbacks in case environment.arcoApiUrl is undefined
    let apiUrl = environment.arcoApiUrl;
    if (!apiUrl) {
      console.warn('environment.arcoApiUrl is undefined, using fallback');
      apiUrl = 'https://test.arco.sa/api';
    }

    const loginUrl = `${apiUrl}/security/CheckLoginForToken?UserName=${username}&Password=${password}`;

    console.log('Login URL:', loginUrl);
    console.log('Making HTTP request to:', loginUrl);

    return this._http.get<any>(loginUrl).pipe(
      tap(response => {
        console.log('Login API response:', response);
      }),
      catchError(error => {
        console.error('Login API error:', error);
        console.error('Error status:', error.status);
        console.error('Error message:', error.message);
        console.error('Error URL:', error.url);
        throw error;
      })
    );
  }

  getJwtTokenFromMicrosoftToken(token) {
    return this._http.get<any>(environment.arcoApiUrl + `/security/GenerateSecurityTokenByADToken?id_token=${token}`);
  }

  // Method to exchange Microsoft token for application token
  exchangeMicrosoftTokenForAppToken(msalToken: string) {
    return this._http.get<any>(environment.arcoApiUrl + `/security/GenerateSecurityTokenByADToken?id_token=${msalToken}`);
  }

  // Method to get Microsoft user profile
  getMicrosoftUserProfile(accessToken: string) {
    return this._http.get<any>('https://graph.microsoft.com/v1.0/me', {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
  }

  endSession() {
    localStorage.setItem('sessionEnded', 'sessionEnded');
  }
  /* #endregion */
}
